import { theme as antdTheme } from 'antd';
import type { ThemeConfig } from 'antd';

export const DEFAULT_PRIMARY_COLOR = '#538abc';

/**
 * Brand primary for React roots mounted outside `ThemeColorProvider` (e.g. `createRoot` portals),
 * read from the `--primary` custom property that provider publishes on `:root`.
 */
export function resolveAppPrimaryColor(): string {
    if (typeof window === 'undefined') return DEFAULT_PRIMARY_COLOR;
    const value = getComputedStyle(document.documentElement).getPropertyValue('--primary').trim();
    return value || DEFAULT_PRIMARY_COLOR;
}

/**
 * Single source of truth for the antd theme. Detached roots must build the same config as `App`,
 * otherwise their controls fall back to antd defaults (#1677ff primary, 32px controls, system font).
 */
export function buildAntdThemeConfig(mode: 'dark' | 'light', primary: string): ThemeConfig {
    const isDark = mode === 'dark';

    return {
        algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: {
            colorPrimary: primary,
            colorBgBase: isDark ? '#141414' : '#fafbfc',
            colorBgContainer: isDark ? '#262626' : '#ffffff',
            colorBgElevated: isDark ? '#303030' : '#ffffff',
            colorText: isDark ? 'rgba(255,255,255,0.9)' : '#171a1f',
            colorTextSecondary: isDark ? 'rgba(255,255,255,0.55)' : '#565d6d',
            colorTextTertiary: isDark ? 'rgba(255,255,255,0.45)' : '#9095a1',
            colorBorder: isDark ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb',
            colorBorderSecondary: isDark ? 'rgba(255, 255, 255, 0.08)' : '#eef0f3',
            borderRadius: 8,
            wireframe: false,
            fontFamily: "'Inter', sans-serif",
            fontSize: 14,
            fontSizeLG: 16,
            fontSizeSM: 12,
            lineHeight: 1.5715,
            controlHeight: 40,
            controlHeightLG: 48,
            controlHeightSM: 32,
            paddingContentHorizontal: 16,
            paddingContentVertical: 12,
            padding: 16,
            paddingSM: 12,
            paddingXS: 8,
            paddingLG: 24,
            marginXS: 8,
            marginSM: 12,
            margin: 16,
            marginMD: 20,
            marginLG: 24,
            marginXL: 32,
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
            boxShadowSecondary: '0 4px 16px rgba(15, 23, 42, 0.08)',
            /**
             * Dashboard / editor overlays use Modal zIndex ~9600–12000+.
             * antd warns when Select/Dropdown popup zIndex exceeds zIndexPopupBase + ~1100;
             * keep the base near our modal band so nested SelectLike stays in range.
             */
            zIndexPopupBase: 12000,
        },
        components: {
            Button: {
                paddingInline: 16,
                paddingBlock: 8,
                contentFontSize: 14,
                primaryShadow: 'none',
                defaultShadow: 'none',
                ...(isDark
                    ? {
                          defaultBg: '#262626',
                          defaultBorderColor: 'rgba(255, 255, 255, 0.1)',
                          defaultColor: 'rgba(255, 255, 255, 0.88)',
                          defaultHoverBg: '#303030',
                          defaultHoverBorderColor: 'rgba(255, 255, 255, 0.14)',
                          defaultHoverColor: '#ffffff',
                          defaultActiveBg: '#303030',
                          defaultActiveBorderColor: 'rgba(255, 255, 255, 0.14)',
                      }
                    : {}),
            },
            Select: {
                ...(isDark
                    ? {
                          selectorBg: '#262626',
                          optionSelectedBg: `color-mix(in srgb, ${primary} 22%, #262626)`,
                          optionActiveBg: `color-mix(in srgb, ${primary} 16%, #303030)`,
                      }
                    : {}),
            },
            Input: {
                paddingBlock: 10,
                paddingInline: 14,
            },
            Table: {
                headerBg: isDark ? '#262626' : '#f4f6f8',
                headerColor: isDark ? 'rgba(255,255,255,0.9)' : '#171a1f',
                headerSplitColor: 'transparent',
                borderColor: isDark ? '#334155' : '#e8eaef',
                cellPaddingBlock: 12,
                cellPaddingInline: 16,
            },
            Modal: {
                paddingMD: 24,
                paddingContentHorizontal: 24,
            },
            /* Sit above pricing/AHJ modals (9600–9610), drawers (~9250), editor modals (~10020), fixed header (10050) */
            Notification: {
                zIndexPopup: 20000,
            },
            Message: {
                zIndexPopup: 11000,
            },
            Card: {
                paddingLG: 24,
            },
            Tabs: {
                horizontalMargin: '0 0 12px 0',
            },
            Form: {
                itemMarginBottom: 16,
                labelFontSize: 13,
            },
        },
    };
}
